import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useProjectsApi } from 'api/data/projects/projects-api';
import { resolve } from 'api/resolve';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';
import { displayMutationError } from 'utils/strings/showErrorString';

import { getConnectedDataNodesQueryKeyPrefix } from './useConnectedDataNodes';
import { getDataNodesByTypeQueryKeyPrefix } from './useDataNodesByType';

export interface UseAddProjectNodeOptions {
  /**
   * Node-type technical name, used to invalidate this type's row cache and the connected-nodes
   * cache on success. Every caller should supply it — these caches are shared across views (e.g.
   * the single-node grid and the decomposition tree can both list the same type), so skipping
   * invalidation here would leave other views stale after an add made from elsewhere.
   */
  technicalName?: string;
  /**
   * Node-type display name, used for a success toast reading `Added a new ${displayName}.`. Omit
   * to skip the toast — e.g. the decomposition tree reflects an add directly in the tree itself
   * and would otherwise show a redundant one.
   */
  displayName?: string;
}

/**
 * Creates one data-node instance of a blueprint node type.
 *
 * `POST api/projects/{projectId}/nodes` is atomic and synchronous — the backend applies each
 * property's blueprint default and reserves the next AutoID, then returns the created node — unlike
 * the legacy query-grid create, which is event-driven and reports back over SignalR.
 *
 * No optimistic update: the row's server-assigned AutoID and defaults aren't known until the response
 * lands, and inventing a placeholder row would show the user values that may not match what was
 * stored. Instead, on success this invalidates every cache that could be showing this node type's rows
 * (the create response shape differs from the list response, so it can't simply be spliced in), and
 * optionally shows a success toast when the caller supplies a `displayName`.
 */
export function useAddProjectNode(
  projectId: string,
  templateNodeId: string | undefined,
  proposalId?: string,
  { technicalName, displayName }: UseAddProjectNodeOptions = {}
) {
  const { addNode } = useProjectsApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!templateNodeId) {
        throw new Error('Cannot add an instance: no node type selected.');
      }
      return resolve(addNode(projectId, templateNodeId, proposalId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getDataNodesByTypeQueryKeyPrefix(projectId, technicalName, proposalId),
        exact: false
      });
      queryClient.invalidateQueries({
        queryKey: getConnectedDataNodesQueryKeyPrefix(projectId, proposalId),
        exact: false
      });
      if (displayName) {
        SNACKBAR_STORE.getState().showSnackbar(`Added a new ${displayName}.`, 'success', true);
      }
    },
    onError: (error) => {
      // Surface the backend's reason (permissions, validation) rather than a flat "it failed" —
      // `resolve` rejects with the API's own error string.
      SNACKBAR_STORE.getState().showSnackbar(displayMutationError('add', error), 'error', true);
    }
  });
}
